import { jobApplicationStore } from 'ats/pages/pipeline/components/JobApplicationStore';
import {
	getJobApplicationTaskTheme,
	getJobActivityTaskStatusIcon
} from 'ats/utils/jobApplicationTaskTheme';
import moment from 'moment';
import React, { useState } from 'react';
import useIsClamped from 'ats/hooks/useIsClamped';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { formatTimeAgo } from 'shared/utils';
import ApplicantStageIndicator from 'ats/components/applicant-stage-indicator/ApplicantStageIndicator';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import { authStore } from 'shared/security/AuthStore';
import LinkifyText from 'ats/components/linkify-text/LinkifyText';
import { PIN_SCOPES, isTaskPinned } from 'ats/utils/taskPin';
import { getTaskMenuItems } from 'ats/utils/taskActions';
import { formatOccurredOn } from 'ats/pages/pipeline/components/JobApplicationTaskOccurredOnField';

const COMMENT_PREFIX = 'Comment:';

const renderTaskDescription = description => {
	if (description?.startsWith(COMMENT_PREFIX)) {
		return (
			<>
				<span className="task-card-description-label">{COMMENT_PREFIX}</span>
				<LinkifyText text={description.slice(COMMENT_PREFIX.length)} />
			</>
		);
	}

	return <LinkifyText text={description} />;
};

export default function TaskCard({
	task,
	onSuccess,
	showApplicant = true,
	onApplicantNameClick
}) {
	const [showMore, setShowMore] = useState(false);
	const [descriptionRef, isClamped] = useIsClamped(
		task?.description,
		!showMore
	);

	const getType = value => {
		return jobApplicationStore.taskOptions.find(task => task.value === value);
	};

	const getApplicantStage = value =>
		applicantStore.stageOptions.find(stage => stage.value === value);

	const getApplicantStatus = value =>
		applicantStore.statusOptions.find(status => status.value === value);

	const getMenuItems = task =>
		getTaskMenuItems({ scope: PIN_SCOPES.JOB, task, onSuccess });

	const applicantStageData = getApplicantStage(task?.applicant.stage);
	const applicantStatusData = getApplicantStatus(task?.applicant.status);
	const taskTheme = getJobApplicationTaskTheme(task?.type);

	return (
		<div
			data-task-id={task.id}
			className={`job-position-task-card job-activity-task ${taskTheme.className} ${isTaskPinned(task) ? 'is-pinned' : ''}`}>
			<div className="task-card-info-container">
				<div className="task-card-info">
					<div className="task-card-info-header">
						{showApplicant && (
							<>
								<div className="task-card-applicant-info">
									<ApplicantStageIndicator
										stageLabel={applicantStageData?.label}
										statusLabel={applicantStatusData?.label}
										stageLevel={applicantStageData?.level}
										statusColor={applicantStatusData?.color}
									/>
									<h5
										onClick={() => onApplicantNameClick?.(task?.applicant?.id)}>
										{task?.applicant.name ? task?.applicant.name : ''}
									</h5>
								</div>
							</>
						)}
						<div className="task-card-type">
							<img src={taskTheme.icon} alt="" />
							<span>{getType(task?.type)?.label}</span>
						</div>
						<div className="task-card-status">
							<img
								src={getJobActivityTaskStatusIcon(task?.status)}
								alt="task"
							/>
							<span className={`is-capitalized task-status-${task?.status}`}>
								{task?.status}
							</span>
						</div>
					</div>
					<div className="task-card-info-schedule">
						{task?.assignments?.length > 0 && (
							<div className="task-card-info-schedule-assignments">
								{task?.assignments?.map(assignment => (
									<UserProfileAvatar
										imageUrl={assignment?.imageUrl}
										name={assignment?.name}
										tooltipPosition="right"
									/>
								))}
							</div>
						)}
						{jobApplicationStore.isMilestoneTaskType(task?.type) ? (
							<span>
								<strong>
									{jobApplicationStore.getOccurredOnLabel(task?.type)}:{' '}
								</strong>{' '}
								{formatOccurredOn(task?.occurredOn)}
							</span>
						) : (
							<span>
								<strong>Scheduled: </strong>{' '}
								{task?.scheduledDate
									? moment(task?.scheduledDate).format('lll')
									: 'N/A'}
							</span>
						)}
					</div>
				</div>
				<div className="task-card-info actions">
					<span className="task-info-updated-by">
						<UserProfileAvatar
							imageUrl={task?.updatedBy?.imageUrl}
							name={task?.updatedBy?.name}
						/>
						{formatTimeAgo(new Date(task?.updatedAt), {
							moreThanOneDay: true
						})}
					</span>
					<HoverMenu
						id={task.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(task)}
					/>
					{task?.status === 'pending' &&
						task?.assignments?.some(a => a.id === authStore.profile?.id) && (
							<PendingTaskBadge count={1} variant="dot" />
						)}
				</div>
			</div>
			{task?.description && (
				<div className="task-card-description">
					<p
						ref={descriptionRef}
						className={showMore ? undefined : 'is-clamped'}>
						{renderTaskDescription(task?.description)}
					</p>
					{(showMore || isClamped) && (
						<button onClick={() => setShowMore(!showMore)}>
							{showMore ? 'View Less' : 'View More'}
						</button>
					)}
				</div>
			)}
		</div>
	);
}
